/**
 * G007 — 建筑实体与 plan_building 命令。
 *
 * 生命周期从 'planned' 起步（施工/运转属 G008+）。命令是唯一入口：
 * 渲染层的预览与合法性反馈只做【干跑】（复用本模块导出的纯校验），
 * 确认时由模拟内核在 Tick 内重新完整校验并原子落账 ——
 * 预览通过≠命令通过，两次校验之间世界可能已经变了。
 *
 * 拒绝一律走稳定枚举码；任何拒绝路径都不得改动世界状态。
 */

import type { BuildCellId } from '../spatial/types.js';
import { checkPlacement, occupyCells, type PlacementRejection, type PlacementSpecShape } from '../spatial/ops.js';
import type { CommandHandler } from '../system.js';

/** 建筑生命周期。G008 起：planned -> under_construction -> operational；
    operational -> damaged 是损坏前的基础状态转换（灾害触发属后续 Goal）。 */
export type BuildingLifecycle = 'planned' | 'under_construction' | 'operational' | 'damaged';

/** 实体组件表中 building 组件的桶名。 */
export const BUILDING_COMPONENT = 'building';

/** 挂在 entities.components.building[entityId] 下的纯数据组件。
    材料三态账本：reserved（预留出库、在途）→ delivered（已送达工地）→
    consumed（已随施工消耗）。三者之和恒等于造价 —— 材料守恒的依据。 */
export interface BuildingComponent {
  readonly defId: string;
  readonly anchor: BuildCellId;
  /** 朝向档位：0..rotationSteps-1（极坐标网格下为纯视觉朝向，不改 footprint） */
  readonly rotationStep: number;
  lifecycle: BuildingLifecycle;
  /** footprint 展开后占用的全部单元格（放置时冻结，拆除时按此释放） */
  readonly cells: readonly BuildCellId[];
  /** 预留未送达（库存已扣出，尚在配送） */
  reserved: Record<string, number>;
  /** 已送达工地、未随施工消耗 */
  delivered: Record<string, number>;
  /** 已随施工进度消耗（取消时按 refundBasisPoints 折算退款） */
  consumed: Record<string, number>;
  /** 总工时（分钟）与已完成工时 */
  readonly workMinutes: number;
  progressMinutes: number;
  /** 当前配送阻塞原因（稳定码；null = 通畅） */
  blocked: string | null;
}

/** plan_building 校验所需的最小建筑定义形状（game-data BuildingDefinition 的子集投影）。 */
export interface PlanBuildingSpec extends PlacementSpecShape {
  readonly id: string;
  readonly rotationSteps: number;
  readonly cost: Readonly<Record<string, number>>;
  /** 施工总工时（分钟）。G008 起必填。 */
  readonly workMinutes: number;
  /** 取消/拆除时已消耗部分的退款率（万分比，5000 = 50%）。 */
  readonly refundBasisPoints: number;
}

export const PLAN_BUILDING = 'plan_building';

export interface PlanBuildingPayload {
  readonly defId: string;
  readonly anchor: BuildCellId;
  readonly rotationStep: number;
}

/** G007 新增的稳定拒绝码（叠加在 spatial 的 BUILD_* 之上）。 */
export type PlanRejection =
  | PlacementRejection
  | 'BUILD_DEF_UNKNOWN'
  | 'BUILD_ANCHOR_INVALID'
  | 'BUILD_ROTATION_INVALID'
  | 'BUILD_RESOURCES_INSUFFICIENT';

const LAYERS = new Set(['floor', 'wall', 'subsurface']);

function isValidAnchorShape(a: unknown): a is BuildCellId {
  if (typeof a !== 'object' || a === null) return false;
  const c = a as Record<string, unknown>;
  return (
    typeof c['layer'] === 'string' && LAYERS.has(c['layer'])
    && Number.isInteger(c['ring']) && (c['ring'] as number) >= 0
    && Number.isInteger(c['sector']) && (c['sector'] as number) >= 0
  );
}

/** 资源是否足额（缺什么返回缺额清单；空数组 = 够）。渲染层干跑同样用它。 */
export function resourceShortages(
  stock: Readonly<Record<string, number>>,
  cost: Readonly<Record<string, number>>,
): string[] {
  const lack: string[] = [];
  for (const [res, amount] of Object.entries(cost)) {
    if ((stock[res] ?? 0) < amount) lack.push(res);
  }
  return lack;
}

/**
 * plan_building 处理器工厂。定义查找以依赖注入进来 ——
 * simulation-core 不 import game-data（分层：内核不认识内容管线）。
 */
export function createPlanBuildingHandler(
  getDef: (defId: string) => PlanBuildingSpec | undefined,
): CommandHandler<PlanBuildingPayload> {
  return {
    type: PLAN_BUILDING,
    apply(ctx, payload) {
      const def = payload && typeof payload.defId === 'string' ? getDef(payload.defId) : undefined;
      if (!def) return { accepted: false, rejectionCode: 'BUILD_DEF_UNKNOWN' };
      if (!isValidAnchorShape(payload.anchor)) {
        return { accepted: false, rejectionCode: 'BUILD_ANCHOR_INVALID' };
      }
      if (
        !Number.isInteger(payload.rotationStep)
        || payload.rotationStep < 0
        || payload.rotationStep >= def.rotationSteps
      ) {
        return { accepted: false, rejectionCode: 'BUILD_ROTATION_INVALID' };
      }

      const placement = checkPlacement(ctx.state.spatial, def, payload.anchor);
      if (!placement.ok || !placement.cells) {
        return { accepted: false, rejectionCode: placement.rejection ?? 'BUILD_OUT_OF_BOUNDS' };
      }

      const stock = ctx.state.resources.stock as Record<string, number>;
      if (resourceShortages(stock, def.cost).length > 0) {
        return { accepted: false, rejectionCode: 'BUILD_RESOURCES_INSUFFICIENT' };
      }

      /* ---- 全部校验通过，原子落账（此后不允许再失败） ---- */
      const entityId = ctx.ids.allocEntity();
      if (!occupyCells(ctx.state.spatial, placement.cells, entityId)) {
        // checkPlacement 刚确认过空闲，同一 Tick 内被占只可能是内核缺陷
        return { accepted: false, rejectionCode: 'BUILD_CELL_OCCUPIED' };
      }
      for (const [res, amount] of Object.entries(def.cost)) {
        stock[res] = (stock[res] ?? 0) - amount;
      }

      const component: BuildingComponent = {
        defId: def.id,
        anchor: { ...payload.anchor },
        rotationStep: payload.rotationStep,
        lifecycle: 'planned',
        cells: placement.cells.map((c) => ({ ...c })),
        // G008 预留制：出库即预留，配送系统逐批把 reserved 搬到 delivered
        reserved: { ...def.cost },
        delivered: {},
        consumed: {},
        workMinutes: def.workMinutes,
        progressMinutes: 0,
        blocked: null,
      };
      const entities = ctx.state.entities;
      entities.order.push(entityId);
      entities.kind[entityId] = 'building';
      const bucket = (entities.components[BUILDING_COMPONENT] ??= {});
      bucket[entityId] = component as unknown as Record<string, unknown>;

      ctx.emit({
        type: 'BuildingPlanned',
        payload: {
          entityId,
          defId: def.id,
          anchor: component.anchor,
          rotationStep: component.rotationStep,
          cells: component.cells,
        },
      });
      return { accepted: true };
    },
  };
}

export const SEED_BUILDING = 'seed_building';

export interface SeedBuildingPayload {
  readonly defId: string;
  readonly anchor: BuildCellId;
  readonly rotationStep: number;
}

export type SeedRejection =
  | PlacementRejection
  | 'SEED_DEF_UNKNOWN'
  | 'SEED_ANCHOR_INVALID'
  | 'SEED_ROTATION_INVALID';

/**
 * seed_building — 场景预置建筑。
 *
 * 存档载入前的既有殖民地不是玩家建的：它不该扣当期库存，也不该走一遍配送与
 * 施工。但它同样是领域状态修改，因此必须是命令而不是直接改 WorldState —— 这样
 * 预置过程本身可重放、进状态哈希、被快照如实记录。
 *
 * 与 plan_building 的差别仅在于：不校验资源、不扣账、直接落到 operational，
 * 材料账本记在 consumed（等价于"已经建成并消耗完毕"），因此拆除退款照常成立。
 */
export function createSeedBuildingHandler(
  getDef: (defId: string) => PlanBuildingSpec | undefined,
): CommandHandler<SeedBuildingPayload> {
  return {
    type: SEED_BUILDING,
    apply(ctx, payload) {
      const def = payload && typeof payload.defId === 'string' ? getDef(payload.defId) : undefined;
      if (!def) return { accepted: false, rejectionCode: 'SEED_DEF_UNKNOWN' };
      if (!isValidAnchorShape(payload.anchor)) {
        return { accepted: false, rejectionCode: 'SEED_ANCHOR_INVALID' };
      }
      if (
        !Number.isInteger(payload.rotationStep)
        || payload.rotationStep < 0
        || payload.rotationStep >= def.rotationSteps
      ) {
        return { accepted: false, rejectionCode: 'SEED_ROTATION_INVALID' };
      }

      const placement = checkPlacement(ctx.state.spatial, def, payload.anchor);
      if (!placement.ok || !placement.cells) {
        return { accepted: false, rejectionCode: placement.rejection ?? 'BUILD_OUT_OF_BOUNDS' };
      }

      const entityId = ctx.ids.allocEntity();
      if (!occupyCells(ctx.state.spatial, placement.cells, entityId)) {
        return { accepted: false, rejectionCode: 'BUILD_CELL_OCCUPIED' };
      }

      const component: BuildingComponent = {
        defId: def.id,
        anchor: { ...payload.anchor },
        rotationStep: payload.rotationStep,
        lifecycle: 'operational',
        cells: placement.cells.map((c) => ({ ...c })),
        reserved: {},
        delivered: {},
        consumed: { ...def.cost },      // 视作已建成消耗，拆除退款基数照常
        workMinutes: def.workMinutes,
        progressMinutes: def.workMinutes,
        blocked: null,
      };
      const entities = ctx.state.entities;
      entities.order.push(entityId);
      entities.kind[entityId] = 'building';
      const bucket = (entities.components[BUILDING_COMPONENT] ??= {});
      bucket[entityId] = component as unknown as Record<string, unknown>;

      ctx.emit({
        type: 'BuildingSeeded',
        payload: {
          entityId,
          defId: def.id,
          anchor: component.anchor,
          rotationStep: component.rotationStep,
          cells: component.cells,
        },
      });
      return { accepted: true };
    },
  };
}

/** 读取全部 building 组件（投影/渲染用；只读语义）。 */
export function listBuildings(
  entities: { order: readonly string[]; components: Record<string, Record<string, unknown>> },
): Array<{ entityId: string; building: BuildingComponent }> {
  const bucket = entities.components[BUILDING_COMPONENT];
  if (!bucket) return [];
  const out: Array<{ entityId: string; building: BuildingComponent }> = [];
  for (const id of entities.order) {
    const c = bucket[id];
    if (c) out.push({ entityId: id, building: c as unknown as BuildingComponent });
  }
  return out;
}
